// /admin — internal safety/usage dashboard (Phase 6).
// Token-gated via X-Admin-Token (sessionStorage; never in localStorage).

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  BookmarkCheck,
  Loader2,
  LogOut,
  MessageSquareText,
  Phone,
  ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "callgod_admin_token";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return ""; // unparseable date — render nothing
  }
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="cg-card rounded-2xl p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" strokeWidth={1.8} />
        <p className="text-[11px] uppercase tracking-[0.18em]">{label}</p>
      </div>
      <p className="font-serif-display mt-2 text-3xl text-foreground">{value}</p>
      {sub ? <p className="mt-1 text-[12px] text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => {
    try {
      return sessionStorage.getItem(TOKEN_KEY) || "";
    } catch {
      return ""; // storage unavailable — show the gate
    }
  });
  const [input, setInput] = useState("");
  const [authError, setAuthError] = useState(false);
  const [overview, setOverview] = useState(null);
  const [safetyEvents, setSafetyEvents] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [elevenUsage, setElevenUsage] = useState(null);

  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* storage unavailable */
    }
    setToken("");
    setOverview(null);
  }, []);

  // Loading is derived — avoids setState-in-effect.
  const loading = Boolean(token) && !overview && !authError;

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const headers = { "X-Admin-Token": token };
        const [ovRes, seRes, fbRes] = await Promise.all([
          fetch(`${API}/admin/overview`, { headers }),
          fetch(`${API}/admin/safety_events?limit=50`, { headers }),
          fetch(`${API}/admin/feedback?limit=50`, { headers }),
        ]);
        if (ovRes.status === 401) {
          if (!cancelled) {
            setAuthError(true);
            logout();
          }
          return;
        }
        const ov = await ovRes.json();
        const se = await seRes.json();
        const fb = await fbRes.json();
        if (!cancelled) {
          setOverview(ov);
          setSafetyEvents(se.safety_events || []);
          setFeedback(fb.feedback || []);
        }
        fetch(`${API}/admin/eleven/usage`, { headers })
          .then((r) => (r.ok ? r.json() : null))
          .then((u) => { if (!cancelled && u) setElevenUsage(u); })
          .catch(() => { /* non-blocking */ });
      } catch {
        if (!cancelled) setAuthError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, logout]);

  const submitToken = (e) => {
    e.preventDefault();
    const t = input.trim();
    if (!t) return;
    setAuthError(false);
    try {
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch {
      /* storage unavailable — token kept in memory only */
    }
    setToken(t);
  };

  // ---------- token gate ----------
  if (!token) {
    return (
      <main
        data-testid="admin-gate"
        className="flex min-h-screen items-center justify-center px-5"
      >
        <div className="cg-card w-full max-w-sm rounded-2xl p-7">
          <div className="flex items-center gap-2 text-muted-foreground">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.8} />
            <p className="text-[11px] uppercase tracking-[0.2em]">Admin access</p>
          </div>
          <h1 className="font-serif-display mt-2 text-2xl text-foreground">
            Call God — dashboard
          </h1>
          <form onSubmit={submitToken} className="mt-5">
            <label htmlFor="admin-token" className="text-sm text-foreground/85">
              Access token
            </label>
            <input
              id="admin-token"
              data-testid="admin-token-input"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
              className="mt-1.5 w-full rounded-xl border bg-white/70 px-3.5 py-2.5 text-sm text-foreground outline-none"
              style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}
            />
            {authError ? (
              <p
                data-testid="admin-auth-error"
                className="mt-2 text-[13px]"
                style={{ color: "hsl(0 60% 42%)" }}
              >
                That token was not accepted.
              </p>
            ) : null}
            <button
              type="submit"
              data-testid="admin-token-submit"
              className="cg-btn-primary mt-4 w-full !min-h-[46px] text-sm"
            >
              Enter
            </button>
          </form>
          <Link
            to="/"
            className="mt-4 inline-block text-[13px] text-muted-foreground underline underline-offset-4"
          >
            Back to Call God
          </Link>
        </div>
      </main>
    );
  }

  // ---------- dashboard ----------
  return (
    <main
      data-testid="admin-dashboard"
      className="mx-auto w-full max-w-5xl px-5 pb-16 pt-8 lg:px-8"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            Internal
          </p>
          <h1 className="font-serif-display mt-1 text-3xl text-foreground">
            Safety & usage
          </h1>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/voice"
            data-testid="admin-voice-link"
            className="cg-btn-ghost !min-h-[40px] !px-4 text-[13px]"
          >
            <span className="inline-flex items-center gap-1.5">Voice & Phone →</span>
          </Link>
          <Link
            to="/admin/billboards"
            data-testid="admin-billboards-link"
            className="cg-btn-ghost !min-h-[40px] !px-4 text-[13px]"
          >
            <span className="inline-flex items-center gap-1.5">Billboards →</span>
          </Link>
          <Link
            to="/admin/marketing"
            data-testid="admin-marketing-link"
            className="cg-btn-ghost !min-h-[40px] !px-4 text-[13px]"
          >
            <span className="inline-flex items-center gap-1.5">Marketing →</span>
          </Link>
          <button
            type="button"
            data-testid="admin-logout"
            onClick={logout}
            className="cg-btn-ghost !min-h-[40px] !px-4 text-[13px]"
          >
            <span className="inline-flex items-center gap-1.5">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {elevenUsage && elevenUsage.status !== "ok" ? (
        <div
          data-testid="eleven-usage-banner"
          className={`mt-5 rounded-lg border px-4 py-3 text-[13px] ${
            elevenUsage.status === "critical"
              ? "border-red-500/40 bg-red-500/10 text-red-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-300"
          }`}
        >
          ElevenLabs credits at {elevenUsage.pct_used}% of the {elevenUsage.tier} plan.{" "}
          {elevenUsage.status === "critical"
            ? "Voices will go silent when it runs out — upgrade or top up now."
            : "Consider upgrading before it runs out."}{" "}
          <Link to="/admin/voice" className="underline">Voice & Phone →</Link>
        </div>
      ) : null}

      {overview ? (
        <>
          <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={Phone}
              label="Sessions"
              value={overview.sessions.total}
              sub={`${overview.sessions.ended} ended · ${overview.sessions.phone} phone · avg ${Math.round(
                (overview.sessions.duration.avg_ms || 0) / 1000,
              )}s${
                overview.sessions.by_persona && Object.keys(overview.sessions.by_persona).length
                  ? ` · ${Object.entries(overview.sessions.by_persona).map(([k, v]) => `${k}: ${v}`).join(" / ")}`
                  : ""
              }`}
            />
            <StatCard
              icon={AlertTriangle}
              label="Safety events"
              value={overview.safety_events.total}
              sub={Object.entries(overview.safety_events.by_severity)
                .map(([k, v]) => `${k}: ${v}`)
                .join(" · ") || "none"}
            />
            <StatCard
              icon={MessageSquareText}
              label="Feedback"
              value={overview.feedback.total}
              sub={Object.entries(overview.feedback.by_rating)
                .map(([k, v]) => `${k}: ${v}`)
                .join(" · ") || "none"}
            />
            <StatCard
              icon={BookmarkCheck}
              label="Reflections"
              value={overview.reflections.total}
            />
          </div>

          <section className="mt-10">
            <h2 className="font-serif-display text-xl text-foreground">
              Recent safety events
            </h2>
            <div className="cg-card mt-3 overflow-x-auto rounded-2xl">
              <table className="w-full text-left text-[13px]" data-testid="safety-table">
                <thead>
                  <tr
                    className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground"
                    style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}
                  >
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Severity</th>
                    <th className="px-4 py-3">Summary</th>
                    <th className="px-4 py-3">Session</th>
                  </tr>
                </thead>
                <tbody>
                  {safetyEvents.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-5 text-muted-foreground">
                        No safety events recorded.
                      </td>
                    </tr>
                  ) : (
                    safetyEvents.map((ev) => (
                      <tr
                        key={ev.id}
                        style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                          {formatDate(ev.created_at)}
                        </td>
                        <td className="px-4 py-3">{ev.category}</td>
                        <td className="px-4 py-3">
                          <span
                            className="rounded-full px-2.5 py-0.5 text-[11px] uppercase tracking-wide"
                            style={
                              ev.severity === "high"
                                ? { background: "hsla(0,60%,90%,0.9)", color: "hsl(0 60% 35%)" }
                                : { background: "hsla(42,60%,88%,0.9)", color: "hsl(35 60% 30%)" }
                            }
                          >
                            {ev.severity}
                          </span>
                        </td>
                        <td className="max-w-[280px] px-4 py-3 text-foreground/85">
                          {ev.summary || "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                          {ev.session_id}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-serif-display text-xl text-foreground">
              Recent feedback
            </h2>
            <div className="cg-card mt-3 overflow-x-auto rounded-2xl">
              <table className="w-full text-left text-[13px]" data-testid="feedback-table">
                <thead>
                  <tr
                    className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground"
                    style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}
                  >
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Rating</th>
                    <th className="px-4 py-3">Comment</th>
                    <th className="px-4 py-3">Session</th>
                  </tr>
                </thead>
                <tbody>
                  {feedback.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-5 text-muted-foreground">
                        No feedback yet.
                      </td>
                    </tr>
                  ) : (
                    feedback.map((fb) => (
                      <tr
                        key={fb.id}
                        style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                          {formatDate(fb.created_at)}
                        </td>
                        <td className="px-4 py-3">{fb.rating || "comment"}</td>
                        <td className="max-w-[320px] px-4 py-3 text-foreground/85">
                          {fb.comment || "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                          {fb.session_id || "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-serif-display text-xl text-foreground">
              Product events
            </h2>
            <div className="cg-card mt-3 rounded-2xl p-5" data-testid="analytics-block">
              <div className="flex flex-wrap gap-2">
                {Object.entries(overview.analytics.by_event).map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] text-foreground/80"
                    style={{ borderColor: "hsla(38,25%,75%,0.7)" }}
                  >
                    <Activity className="h-3 w-3" strokeWidth={1.8} />
                    {k}: {v}
                  </span>
                ))}
              </div>
            </div>
          </section>
        </>
      ) : null}
    </main>
  );
}
